import type { SharePayloadEntry } from '@egr/xbox/base-app/managers/ExportManager';

import { BlobWriter, Uint8ArrayReader, ZipWriter } from '@zip.js/zip.js';

import { _sendReply, _fakeMessage } from '../Communication';
import { readBinaryFile } from '../Filesystem/Helper';
import { registerHandler } from '../MessageManager';

import { downloadBlob, downloadUrl, getContent } from '@egr/xbox/utils/Download';
import { doNothing } from '@egr/xbox/utils/Helper';
import { Logger } from '@egr/xbox/utils/errors/Logger';

import { fromBase64 } from '@easterngraphics/wcf/modules/utils/arraybuffer';

function sendDownloadProgress(transactionId?: number): VoidFunction {
    if (transactionId == null) {
        return doNothing;
    }

    const total: number = 1024 * 1024;
    let fetched: number = 0;
    const id: number = window.setInterval(
        (): void => {
            fetched += Math.ceil((total - fetched) * 0.05);
            _fakeMessage({
                transaction_id: -1,
                type: 'progress',
                action: 'request',
                arguments: {
                    time: Date.now(),
                    request_id: transactionId,
                    downloads: [{
                        size: total,
                        fetched
                    }]
                }
            });
        },
        250
    );

    return () => {
        window.clearInterval(id);
    };
}

async function getShareContent(
    action: 'decode' | 'fetch' | 'path',
    value: string,
    transactionId?: number
): Promise<ArrayBuffer | undefined> {
    switch (action) {
        case 'decode':
            return fromBase64(value);
        case 'path':
            return readBinaryFile(value);
        case 'fetch':
            const finished: VoidFunction = sendDownloadProgress(transactionId);
            try {
                return await getContent(value, 'arraybuffer');
            } finally {
                finished();
            }
        default:
            Logger.warn('Unsupported share action: ' + (action as string));
            return Promise.resolve(undefined);
    }
}

export function registerDebugShare(remove: (path: string) => Promise<void>, getCancelCheck: (id: number) => VoidFunction): void {
    registerHandler({
        type: 'share',
        callback: async (event: MessageEvent): Promise<void> => {
            try {
                if (event.data.action === 'message') {
                    alert(event.data.arguments.content);
                    _sendReply(event.data.transaction_id, false, undefined);
                } else if (event.data.action === 'content') {
                    const payloadEntries = event.data.arguments.payload as Array<SharePayloadEntry>;
                    if (payloadEntries.length === 1) {
                        const payload = payloadEntries[0];
                        if (payload.action === 'fetch' && payload.content.startsWith('http')) {
                            downloadUrl(payload.content, payload.filename, '_blank');
                        } else {
                            const content: ArrayBuffer | undefined = await getShareContent(payload.action, payload.content, event.data.transaction_id);
                            downloadBlob(new Blob([content!]), payload.filename);
                        }
                        return _sendReply(event.data.transaction_id, false, undefined);
                    }

                    const cancelCheck: VoidFunction = getCancelCheck(event.data.transaction_id);
                    const zipWriter = new ZipWriter<Blob>(new BlobWriter('application/zip'));

                    for (const payload of event.data.arguments.payload as Array<SharePayloadEntry>) {
                        cancelCheck();
                        const content: ArrayBuffer | undefined = await getShareContent(payload.action, payload.content, event.data.transaction_id);

                        if (content != null) {
                            await zipWriter.add(payload.filename, new Uint8ArrayReader(new Uint8Array(content)));
                        }

                        if (payload.remove_path_after_share === true && payload.action === 'path') {
                            void remove(payload.content).catch(doNothing);
                        }
                    }

                    cancelCheck();
                    const zipContent: Blob = await zipWriter.close();

                    cancelCheck();
                    downloadBlob(zipContent, 'content.zip');
                    _sendReply(event.data.transaction_id, false, undefined);
                } else {
                    const data: ArrayBuffer | undefined = await getShareContent(event.data.action, event.data.arguments.content);
                    if (data != null) {
                        downloadBlob(new Blob([data], {type: event.data.arguments.mimetype}), event.data.arguments.filename);
                        _sendReply(event.data.transaction_id, false, undefined);
                    }
                }
            } catch (error) {
                _sendReply(event.data.transaction_id, true, error.message);
            }
        }
    });

}